/**
 * Request → order → what actually arrived.
 *
 * Nothing on this screen reaches the ledger. A purchase order is a commitment,
 * not a transaction, and a goods receipt records a fact about the loading bay.
 * The accounting happens when the bill arrives, one screen along — which is why
 * the received column here is the one the bill gets checked against.
 *
 * One list at a time — requests, orders, deliveries — each opening beside the
 * list with its detail and the next thing to do. Raising a request or an order
 * opens its form beside the list too, rather than sitting open on the page.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import QuotationsPanel, { type RequisitionRow } from '../components/Quotations'
import { ErrorNote, Field, Loading, Note, Pill, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'
import SearchSelect from '../components/SearchSelect'

type Requisition = RequisitionRow

interface FormOption {
  id: string
  code: string | null
  name: string
  note: string | null
}

interface FormOptions {
  departments: FormOption[]
  categories: FormOption[]
  vendors: FormOption[]
  my_department_id: string | null
}

interface OrderLine {
  id: string
  line_no: number
  description: string
  quantity: string
  rate_paise: number
  amount_paise: number
  //: GST on this line at its own slab; the order's tax is the sum.
  gst_rate: string | null
  tax_paise: number
  total_paise: number
  received_quantity: string
}

interface Order {
  id: string
  reference: string
  party_id: string
  order_date: string
  status: string
  subtotal_paise: number
  tax_paise: number
  total_paise: number
  approval_state: string
  lines: OrderLine[]
}

interface Receipt {
  id: string
  reference: string
  purchase_order_id: string
  received_on: string
  delivery_note: string | null
  line_count: number
}

interface Vendor {
  id: string
  code: string
  name: string
}

interface EditableLine {
  description: string
  quantity: string
  rate: string
  //: GST percentage for this line. Empty means no GST on it.
  gst: string
}

const BLANK: EditableLine = { description: '', quantity: '1', rate: '', gst: '' }

//: The slabs as they stand. A free figure is still allowed for the odd case.
const GST_SLABS = ['0', '5', '12', '18', '28']

function lineAmount(line: EditableLine): number {
  return Math.round(Number(line.quantity || 0) * toPaise(line.rate))
}

function lineTax(line: EditableLine): number {
  const rate = Number(line.gst)
  if (!line.gst.trim() || !Number.isFinite(rate)) return 0
  return Math.round((lineAmount(line) * rate) / 100)
}

/** Rupees as typed by a person → integer paise. Money never round-trips as a float. */
function toPaise(text: string): number {
  const clean = text.replace(/[^0-9.-]/g, '')
  if (!clean) return 0
  return Math.round(Number(clean) * 100)
}

const today = () => new Date().toISOString().slice(0, 10)

interface Step {
  label: string
  tone: string
  detail?: string
}

type Tab = 'requests' | 'orders' | 'deliveries'
type Panel = 'new-request' | 'new-order' | null

export default function Purchasing() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['requisitions'] })
    void queryClient.invalidateQueries({ queryKey: ['orders'] })
    void queryClient.invalidateQueries({ queryKey: ['receipts'] })
  }
  const seeOrders = can('proc.purchase_order.view')

  const [tab, setTab] = useState<Tab>('requests')
  const [panel, setPanel] = useState<Panel>(null)
  const [openRequest, setOpenRequest] = useState<string | null>(null)
  const [openOrder, setOpenOrder] = useState<string | null>(null)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')

  const requisitions = useQuery({
    queryKey: ['requisitions'],
    queryFn: () => api.get<Requisition[]>('/procurement/requisitions'),
  })
  const orders = useQuery({
    queryKey: ['orders'],
    queryFn: () => api.get<Order[]>('/procurement/orders'),
    enabled: seeOrders,
  })
  const receipts = useQuery({
    queryKey: ['receipts'],
    queryFn: () => api.get<Receipt[]>('/procurement/receipts'),
    enabled: seeOrders,
  })
  const vendors = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<Vendor[]>('/parties'),
    enabled: can('party.vendor.view'),
  })
  // Departments, categories and approved vendors, readable by every role that
  // raises a request. The full vendor list is not an Employee's to browse.
  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
    enabled: can('spend.payment_request.view_own'),
  })

  const reqRows = requisitions.data ?? []
  const orderRows = orders.data ?? []
  const receiptRows = receipts.data ?? []
  const vendorName = (id: string) => (vendors.data ?? []).find((v) => v.id === id)?.name ?? '—'
  const departmentName = (id: string | null) =>
    options.data?.departments.find((d) => d.id === id)?.name
  const orderRef = (id: string) => orderRows.find((o) => o.id === id)?.reference ?? '—'

  const requestStep = (r: Requisition): Step => {
    if (r.status === 'draft')
      return can('proc.purchase_request.submit')
        ? {
            label: 'Ready to submit',
            tone: 'turn',
            detail: 'add quotations first if it needs them',
          }
        : { label: 'Draft', tone: 'draft' }
    if (r.status === 'submitted')
      return can('proc.purchase_request.approve')
        ? { label: 'Waiting for you', tone: 'turn', detail: r.approval_state }
        : { label: 'With a checker', tone: 'submitted', detail: r.approval_state }
    if (r.status === 'approved')
      return r.purchase_mode === 'direct'
        ? r.payment_request_reference
          ? { label: 'Payment raised', tone: 'approved', detail: r.payment_request_reference }
          : { label: 'Pay the chosen quotation', tone: 'advisory' }
        : { label: 'Approved — order it', tone: 'advisory' }
    return { label: r.status.replace(/_/g, ' '), tone: r.status }
  }
  const orderStep = (o: Order): Step => {
    if (o.status === 'draft')
      return can('proc.purchase_order.submit')
        ? { label: 'Ready to submit', tone: 'turn' }
        : { label: 'Draft', tone: 'draft' }
    if (o.status === 'submitted')
      return can('proc.purchase_order.approve')
        ? { label: 'Waiting for you', tone: 'turn', detail: o.approval_state }
        : { label: 'With a checker', tone: 'submitted', detail: o.approval_state }
    if (o.status === 'approved')
      return can('proc.purchase_order.approve')
        ? { label: 'Issue to vendor', tone: 'turn' }
        : { label: 'Approved, not yet issued', tone: 'advisory' }
    if (o.status === 'issued' || o.status === 'partially_received')
      return {
        label: o.status === 'issued' ? 'Waiting for the goods' : 'Part received',
        tone: 'advisory',
      }
    return { label: o.status.replace(/_/g, ' '), tone: o.status }
  }

  const shownRequests = reqRows.filter(
    (r) =>
      (!status || r.status === status) &&
      matches(
        search,
        r.reference,
        r.purpose,
        departmentName(r.department_id),
        r.payment_request_reference,
      ),
  )
  const shownOrders = orderRows.filter(
    (o) =>
      (!status || o.status === status) &&
      matches(search, o.reference, vendorName(o.party_id), (o.total_paise / 100).toFixed(2)),
  )
  const shownReceipts = receiptRows.filter((r) =>
    matches(search, r.reference, r.delivery_note, orderRef(r.purchase_order_id)),
  )
  const pagedRequests = usePaged(shownRequests, `${status}|${search}`)
  const pagedOrders = usePaged(shownOrders, `${status}|${search}`)
  const pagedReceipts = usePaged(shownReceipts, search)

  const submitRequest = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/requisitions/${id}/submit`),
    onSuccess: refresh,
  })
  const decideRequest = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api.post(`/procurement/requisitions/${id}/decide?approve=${approve}`),
    onSuccess: refresh,
  })
  // An order is submitted, approved by someone other than whoever raised it,
  // and only then issued to the vendor. Issuing a draft used to be one click.
  const submitOrder = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/orders/${id}/submit`),
    onSuccess: refresh,
  })
  const decideOrder = useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; approve: boolean; reason?: string }) =>
      api.post(
        `/procurement/orders/${id}/decide?approve=${approve}${
          reason ? `&reason=${encodeURIComponent(reason)}` : ''
        }`,
      ),
    onSuccess: refresh,
  })
  const issue = useMutation({
    mutationFn: (id: string) => api.post(`/procurement/orders/${id}/issue`),
    onSuccess: refresh,
  })

  if (requisitions.isLoading) return <Loading what="Loading purchasing" />

  //: The same rule as the Next step column, so the two never disagree.
  const waitingOnMe =
    reqRows.filter((r) => requestStep(r).tone === 'turn').length +
    orderRows.filter((o) => orderStep(o).tone === 'turn').length
  const openOrders = orderRows.filter(
    (o) => o.status === 'issued' || o.status === 'partially_received',
  )
  const toOrder = reqRows.filter((r) => r.status === 'approved' && r.purchase_mode === 'via_po')

  const request = reqRows.find((r) => r.id === openRequest)
  const order = orderRows.find((o) => o.id === openOrder)
  const step = (s: Step) => (
    <div className="next-step">
      <span className={`pill ${s.tone}`}>{s.label}</span>
      {s.detail && <span className="faint">{s.detail}</span>}
    </div>
  )

  const tabs = [
    { key: 'requests' as Tab, label: 'Purchase requests', count: reqRows.length },
    ...(seeOrders
      ? [
          { key: 'orders' as Tab, label: 'Purchase orders', count: orderRows.length },
          { key: 'deliveries' as Tab, label: 'Deliveries', count: receiptRows.length },
        ]
      : []),
  ]
  const statuses =
    tab === 'requests'
      ? Array.from(new Set(reqRows.map((r) => r.status)))
      : Array.from(new Set(orderRows.map((o) => o.status)))

  return (
    <>
      <ErrorNote
        error={
          requisitions.error ??
          orders.error ??
          submitRequest.error ??
          decideRequest.error ??
          submitOrder.error ??
          decideOrder.error ??
          issue.error
        }
      />

      <div className="bank-toolbar">
        {can('proc.purchase_request.create') && (
          <button className="primary" onClick={() => setPanel('new-request')}>
            Raise a request
          </button>
        )}
        {can('proc.purchase_order.create') && (
          <button onClick={() => setPanel('new-order')}>Raise an order</button>
        )}
      </div>

      <div className="grid">
        <Stat
          label="Waiting for you"
          value={String(waitingOnMe)}
          note="requests and orders to submit, approve or issue"
        />
        <Stat
          label="Open requests"
          value={String(
            reqRows.filter((r) => r.status === 'draft' || r.status === 'submitted').length,
          )}
          note={`${toOrder.length} approved and not yet ordered`}
        />
        {seeOrders && (
          <Stat
            label="Orders out with vendors"
            value={String(openOrders.length)}
            note={`${formatInr(openOrders.reduce((t, o) => t + o.total_paise, 0))} committed`}
          />
        )}
        {seeOrders && (
          <Stat
            label="Deliveries recorded"
            value={String(receiptRows.length)}
            note="what the bills are checked against"
          />
        )}
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={tabs}
            value={tab}
            onChange={(t) => {
              setTab(t)
              setStatus('')
            }}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder={
                tab === 'requests'
                  ? 'Reference, purpose, department'
                  : tab === 'orders'
                    ? 'Reference, vendor, amount'
                    : 'Reference, delivery note, order'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search"
            />
            {tab !== 'deliveries' && (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Status"
              >
                <option value="">Any status</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        <div className="body tight">
          {tab === 'requests' &&
            (shownRequests.length === 0 ? (
              <div className="empty">
                {reqRows.length === 0 ? 'Nothing requested yet.' : 'No request matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Needed by</th>
                    <th>How</th>
                    <th className="num">Estimate</th>
                    <th>Status</th>
                    <th>Next step</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRequests.rows.map((r) => (
                    <tr key={r.id} {...rowProps(() => setOpenRequest(r.id), openRequest === r.id)}>
                      <td>
                        <div className="strong">{r.purpose}</div>
                        <div className="cell-sub">
                          <span className="ref">{r.reference}</span>
                          {departmentName(r.department_id) &&
                            ` · ${departmentName(r.department_id)}`}
                        </div>
                      </td>
                      <td className="muted date">
                        {r.needed_by ?? <span className="faint">—</span>}
                      </td>
                      <td className="muted">
                        {r.purchase_mode === 'direct' ? 'Direct' : 'Order'}
                        {r.quotation_count > 0 && (
                          <div className="cell-sub">
                            {r.quotation_count} quotation{r.quotation_count === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                      <td className="num">
                        {formatInr(r.estimated_total_paise)}
                        {r.selected_total_paise !== null && (
                          <div className="cell-sub">chosen {formatInr(r.selected_total_paise)}</div>
                        )}
                      </td>
                      <td>
                        <Pill value={r.status} />
                      </td>
                      <td>{step(requestStep(r))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}

          {tab === 'orders' &&
            (shownOrders.length === 0 ? (
              <div className="empty">
                {orderRows.length === 0 ? 'No orders yet.' : 'No order matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Ordered</th>
                    <th className="num">Lines</th>
                    <th className="num">Total</th>
                    <th>Status</th>
                    <th>Next step</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedOrders.rows.map((o) => (
                    <tr key={o.id} {...rowProps(() => setOpenOrder(o.id), openOrder === o.id)}>
                      <td>
                        <div className="strong">{vendorName(o.party_id)}</div>
                        <div className="cell-sub ref">{o.reference}</div>
                      </td>
                      <td className="muted date">{o.order_date}</td>
                      <td className="num">{o.lines.length}</td>
                      <td className="num">
                        <strong>{formatInr(o.total_paise)}</strong>
                        {o.tax_paise > 0 && (
                          <div className="cell-sub">GST {formatInr(o.tax_paise)}</div>
                        )}
                      </td>
                      <td>
                        <Pill value={o.status} />
                      </td>
                      <td>{step(orderStep(o))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}

          {tab === 'deliveries' &&
            (shownReceipts.length === 0 ? (
              <div className="empty">
                {receiptRows.length === 0 ? 'Nothing received yet.' : 'No delivery matches that.'}
              </div>
            ) : (
              <table className="appr-table">
                <thead>
                  <tr>
                    <th>Delivery</th>
                    <th>Received on</th>
                    <th>Against order</th>
                    <th>Delivery note</th>
                    <th className="num">Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedReceipts.rows.map((r) => (
                    <tr
                      key={r.id}
                      {...rowProps(() => setOpenOrder(r.purchase_order_id), false)}
                      title="Open the order it arrived against"
                    >
                      <td className="ref">{r.reference}</td>
                      <td className="date">{r.received_on}</td>
                      <td>
                        <span className="ref">{orderRef(r.purchase_order_id)}</span>
                        {orderRows.find((o) => o.id === r.purchase_order_id) && (
                          <div className="cell-sub">
                            {vendorName(
                              orderRows.find((o) => o.id === r.purchase_order_id)!.party_id,
                            )}
                          </div>
                        )}
                      </td>
                      <td className="muted">
                        {r.delivery_note ?? <span className="faint">—</span>}
                      </td>
                      <td className="num">{r.line_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </div>
        {tab === 'requests' && (
          <Pager
            page={pagedRequests.page}
            pages={pagedRequests.pages}
            onPage={pagedRequests.setPage}
          />
        )}
        {tab === 'orders' && (
          <Pager page={pagedOrders.page} pages={pagedOrders.pages} onPage={pagedOrders.setPage} />
        )}
        {tab === 'deliveries' && (
          <Pager
            page={pagedReceipts.page}
            pages={pagedReceipts.pages}
            onPage={pagedReceipts.setPage}
          />
        )}
      </section>

      {request && (
        <SidePanel
          kicker="Purchase request"
          title={request.purpose}
          sub={
            <>
              <span className="ref">{request.reference}</span>
              {departmentName(request.department_id) &&
                ` · ${departmentName(request.department_id)}`}
            </>
          }
          amount={formatInr(request.selected_total_paise ?? request.estimated_total_paise)}
          badge={step(requestStep(request))}
          wide
          onClose={() => setOpenRequest(null)}
          foot={
            <>
              <ErrorNote error={submitRequest.error ?? decideRequest.error} />
              {request.status === 'draft' && can('proc.purchase_request.submit') && (
                <button
                  className="primary"
                  disabled={submitRequest.isPending}
                  onClick={() => submitRequest.mutate(request.id)}
                >
                  Submit for approval
                </button>
              )}
              {request.status === 'submitted' && can('proc.purchase_request.approve') && (
                <>
                  <button
                    className="primary"
                    disabled={decideRequest.isPending}
                    onClick={() => decideRequest.mutate({ id: request.id, approve: true })}
                  >
                    Approve
                  </button>
                  <button
                    disabled={decideRequest.isPending}
                    onClick={() => decideRequest.mutate({ id: request.id, approve: false })}
                  >
                    Reject
                  </button>
                </>
              )}
              {request.status === 'submitted' && !can('proc.purchase_request.approve') && (
                <span className="faint">With a checker — nothing for you to do.</span>
              )}
              {request.status === 'approved' &&
                request.purchase_mode === 'via_po' &&
                can('proc.purchase_order.create') && (
                  <button
                    onClick={() => {
                      setOpenRequest(null)
                      setPanel('new-order')
                    }}
                  >
                    Raise its order
                  </button>
                )}
            </>
          }
        >
          <section>
            <h3>The request</h3>
            <dl className="appr-facts">
              <div>
                <dt>Status</dt>
                <dd>
                  <Pill value={request.status} />
                </dd>
              </div>
              <div>
                <dt>Needed by</dt>
                <dd>{request.needed_by ?? '—'}</dd>
              </div>
              <div>
                <dt>How it is bought</dt>
                <dd>
                  {request.purchase_mode === 'direct'
                    ? 'Direct — paid against the chosen quotation'
                    : 'Purchase order, then the bill'}
                </dd>
              </div>
              <div>
                <dt>Estimate</dt>
                <dd>{formatInr(request.estimated_total_paise)}</dd>
              </div>
              {request.selected_total_paise !== null && (
                <div>
                  <dt>Chosen quotation</dt>
                  <dd>{formatInr(request.selected_total_paise)}</dd>
                </div>
              )}
              <div>
                <dt>Approval</dt>
                <dd>{request.approval_state}</dd>
              </div>
              {request.payment_request_reference && (
                <div>
                  <dt>Payment request</dt>
                  <dd className="mono">{request.payment_request_reference}</dd>
                </div>
              )}
            </dl>
            {request.justification && (
              <p className="appr-summary">
                <span className="muted">Why: </span>
                {request.justification}
              </p>
            )}
          </section>
          {can('proc.quotation.view_own') && (
            <section>
              <QuotationsPanel
                requisition={request}
                vendors={options.data?.vendors ?? []}
                onClose={() => setOpenRequest(null)}
                onDone={refresh}
              />
            </section>
          )}
        </SidePanel>
      )}

      {order && (
        <SidePanel
          kicker="Purchase order"
          title={vendorName(order.party_id)}
          sub={
            <>
              <span className="ref">{order.reference}</span> · ordered {order.order_date}
            </>
          }
          amount={formatInr(order.total_paise)}
          badge={step(orderStep(order))}
          wide
          onClose={() => setOpenOrder(null)}
          foot={
            <>
              <ErrorNote error={submitOrder.error ?? decideOrder.error ?? issue.error} />
              {order.status === 'draft' && can('proc.purchase_order.submit') && (
                <button
                  className="primary"
                  disabled={submitOrder.isPending}
                  onClick={() => submitOrder.mutate(order.id)}
                >
                  Submit for approval
                </button>
              )}
              {order.status === 'submitted' && can('proc.purchase_order.approve') && (
                <>
                  <button
                    className="primary"
                    disabled={decideOrder.isPending}
                    onClick={() => decideOrder.mutate({ id: order.id, approve: true })}
                  >
                    Approve
                  </button>
                  <button
                    className="danger"
                    disabled={decideOrder.isPending}
                    onClick={() => {
                      const reason = window.prompt('Why is this order rejected?')
                      if (reason && reason.trim()) {
                        decideOrder.mutate({ id: order.id, approve: false, reason })
                      }
                    }}
                  >
                    Reject
                  </button>
                </>
              )}
              {order.status === 'submitted' && !can('proc.purchase_order.approve') && (
                <span className="faint">With a checker — nothing for you to do.</span>
              )}
              {order.status === 'approved' && can('proc.purchase_order.approve') && (
                <button
                  className="primary"
                  disabled={issue.isPending}
                  onClick={() => issue.mutate(order.id)}
                >
                  Issue to vendor
                </button>
              )}
            </>
          }
        >
          <OrderDetail
            key={order.id}
            order={order}
            receipts={receiptRows.filter((r) => r.purchase_order_id === order.id)}
            onDone={refresh}
          />
        </SidePanel>
      )}

      {panel === 'new-request' && (
        <SidePanel
          kicker="Purchase request"
          title="Raise a request"
          sub="add quotations once it is raised, before submitting"
          wide
          onClose={() => setPanel(null)}
        >
          <RequestForm
            options={options.data}
            departmentName={departmentName}
            onDone={() => {
              setPanel(null)
              setTab('requests')
              refresh()
            }}
          />
        </SidePanel>
      )}

      {panel === 'new-order' && (
        <SidePanel
          kicker="Purchase order"
          title="Raise an order"
          sub="a commitment — nothing posts until the bill"
          wide
          onClose={() => setPanel(null)}
        >
          <OrderForm
            vendors={vendors.data ?? []}
            approved={reqRows.filter((r) => r.status === 'approved')}
            onDone={() => {
              setPanel(null)
              setTab('orders')
              refresh()
            }}
          />
        </SidePanel>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Raising a request — the one thing on this screen an Employee touches
// ---------------------------------------------------------------------------

function RequestForm({
  options,
  departmentName,
  onDone,
}: {
  options: FormOptions | undefined
  departmentName: (id: string | null) => string | undefined
  onDone: () => void
}) {
  const [purpose, setPurpose] = useState('')
  const [neededBy, setNeededBy] = useState('')
  const [lines, setLines] = useState<EditableLine[]>([BLANK])
  const [extra, setExtra] = useState({
    department_id: '',
    category_id: '',
    purchase_mode: 'via_po',
    justification: '',
  })

  const create = useMutation({
    mutationFn: () =>
      api.post('/procurement/requisitions', {
        purpose,
        needed_by: neededBy || null,
        department_id: extra.department_id || null,
        category_id: extra.category_id || null,
        purchase_mode: extra.purchase_mode,
        justification: extra.justification || null,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          estimated_rate_paise: toPaise(l.rate),
        })),
      }),
    onSuccess: () => {
      setPurpose('')
      setLines([BLANK])
      setExtra({ ...extra, justification: '' })
      onDone()
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        create.mutate()
      }}
    >
      <ErrorNote error={create.error} />
      <div className="row">
        <Field label="Purpose">
          <input
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Consumables for the September clinic list"
            required
          />
        </Field>
        <Field label="Needed by">
          <input type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
        </Field>
      </div>
      <div className="row">
        <Field label="Department" caption="decides who approves">
          <select
            value={extra.department_id}
            onChange={(e) => setExtra({ ...extra, department_id: e.target.value })}
          >
            <option value="">
              My department
              {departmentName(options?.my_department_id ?? null)
                ? ` (${departmentName(options?.my_department_id ?? null)})`
                : ''}
            </option>
            {(options?.departments ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Category" caption="where the cost belongs">
          <SearchSelect
            aria-label="Category"
            value={extra.category_id}
            onChange={(v) => setExtra({ ...extra, category_id: v })}
            placeholder="Choose…"
            options={(options?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Field>
        <Field label="How it is bought">
          <select
            value={extra.purchase_mode}
            onChange={(e) => setExtra({ ...extra, purchase_mode: e.target.value })}
          >
            <option value="via_po">Purchase order, then the bill</option>
            <option value="direct">Direct — paid against the chosen quotation</option>
          </select>
        </Field>
      </div>
      <Field label="Justification" caption="why it is needed">
        <textarea
          rows={2}
          value={extra.justification}
          onChange={(e) => setExtra({ ...extra, justification: e.target.value })}
        />
      </Field>
      <LineEditor lines={lines} setLines={setLines} rateLabel="Estimated rate" />
      <div className="row" style={{ marginTop: 12 }}>
        <div className="shrink">
          <button className="primary" type="submit" disabled={create.isPending}>
            Raise request
          </button>
        </div>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Raising an order
// ---------------------------------------------------------------------------

function OrderForm({
  vendors,
  approved,
  onDone,
}: {
  vendors: Vendor[]
  approved: Requisition[]
  onDone: () => void
}) {
  const [form, setForm] = useState({
    party_id: '',
    order_date: today(),
    requisition_id: '',
  })
  const [lines, setLines] = useState<EditableLine[]>([BLANK])
  //: Shown as the person types, and recomputed by the server from the same
  //: rates on save -- the figure on the screen is the figure on the order.
  const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0)
  const tax = lines.reduce((sum, l) => sum + lineTax(l), 0)

  const create = useMutation({
    mutationFn: () =>
      api.post('/procurement/orders', {
        party_id: form.party_id,
        order_date: form.order_date,
        requisition_id: form.requisition_id || null,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          rate_paise: toPaise(l.rate),
          gst_rate: l.gst.trim() ? l.gst.trim() : null,
        })),
      }),
    onSuccess: () => {
      setLines([BLANK])
      setForm({ ...form, requisition_id: '' })
      onDone()
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        create.mutate()
      }}
    >
      <ErrorNote error={create.error} />
      <div className="row">
        <Field label="Vendor">
          <select
            value={form.party_id}
            onChange={(e) => setForm({ ...form, party_id: e.target.value })}
            required
          >
            <option value="">Choose…</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Order date">
          <input
            type="date"
            value={form.order_date}
            onChange={(e) => setForm({ ...form, order_date: e.target.value })}
            required
          />
        </Field>
        <Field label="Against request" caption="optional">
          <select
            value={form.requisition_id}
            onChange={(e) => setForm({ ...form, requisition_id: e.target.value })}
          >
            <option value="">None</option>
            {approved.map((r) => (
              <option key={r.id} value={r.id}>
                {r.reference} — {r.purpose}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <LineEditor lines={lines} setLines={setLines} rateLabel="Rate" withTax />
      <dl className="appr-facts" style={{ marginTop: 12 }}>
        <div>
          <dt>Goods and services</dt>
          <dd>{formatInr(subtotal)}</dd>
        </div>
        <div>
          <dt>GST</dt>
          <dd>{formatInr(tax)}</dd>
        </div>
        <div>
          <dt>Total — what the order commits</dt>
          <dd>
            <strong>{formatInr(subtotal + tax)}</strong>
          </dd>
        </div>
      </dl>
      <div className="row">
        <div className="shrink">
          <button className="primary" type="submit" disabled={create.isPending}>
            Raise order
          </button>
        </div>
      </div>
    </form>
  )
}

/** One order, its lines, what has arrived against each, and a receiving form. */
function OrderDetail({
  order,
  receipts,
  onDone,
}: {
  order: Order
  receipts: Receipt[]
  onDone: () => void
}) {
  const { can } = useAuth()
  const [receivedOn, setReceivedOn] = useState(today())
  const [note, setNote] = useState('')
  const [quantities, setQuantities] = useState<Record<string, string>>({})

  const receive = useMutation({
    mutationFn: () =>
      api.post('/procurement/receipts', {
        purchase_order_id: order.id,
        received_on: receivedOn,
        delivery_note: note || null,
        lines: order.lines
          .filter((l) => Number(quantities[l.id] ?? 0) > 0)
          .map((l) => ({
            purchase_order_line_id: l.id,
            quantity_received: quantities[l.id],
          })),
      }),
    onSuccess: () => {
      setQuantities({})
      setNote('')
      onDone()
    },
  })

  const outstanding = (line: OrderLine) => Number(line.quantity) - Number(line.received_quantity)
  // Goods are received only against an order the vendor was actually sent.
  const receivable = order.status === 'issued' || order.status === 'partially_received'
  const canReceive = can('proc.grn.create') && receivable

  return (
    <>
      <section>
        <dl className="appr-facts">
          <div>
            <dt>Status</dt>
            <dd>
              <Pill value={order.status} />
            </dd>
          </div>
          <div>
            <dt>Goods and services</dt>
            <dd>{formatInr(order.subtotal_paise)}</dd>
          </div>
          <div>
            <dt>GST</dt>
            <dd>{formatInr(order.tax_paise)}</dd>
          </div>
          <div>
            <dt>Approval</dt>
            <dd>{order.approval_state}</dd>
          </div>
        </dl>
      </section>
      <section>
        <h3>Ordered against received</h3>
        <ErrorNote error={receive.error} />
        <div className="scroll-x">
          <table className="appr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th className="num">Ordered</th>
                <th className="num">Received</th>
                <th className="num">Outstanding</th>
                <th className="num">Rate</th>
                <th className="num">GST</th>
                <th className="num">With GST</th>
                {canReceive && <th className="num">Receiving now</th>}
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">{line.line_no}</td>
                  <td>{line.description}</td>
                  <td className="num">{Number(line.quantity)}</td>
                  <td className="num">{Number(line.received_quantity)}</td>
                  <td className="num">
                    {outstanding(line) > 0 ? (
                      <strong>{outstanding(line)}</strong>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">{formatInr(line.rate_paise)}</td>
                  <td className="num">
                    {line.gst_rate !== null ? (
                      <>
                        {formatInr(line.tax_paise)}
                        <div className="faint">{Number(line.gst_rate)}%</div>
                      </>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td className="num">{formatInr(line.total_paise)}</td>
                  {canReceive && (
                    <td className="num">
                      <input
                        className="num"
                        style={{ width: 90 }}
                        value={quantities[line.id] ?? ''}
                        onChange={(e) =>
                          setQuantities({ ...quantities, [line.id]: e.target.value })
                        }
                        placeholder="0"
                        aria-label={`Receiving now, line ${line.line_no}`}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {receipts.length > 0 && (
          <Note kind="info">
            Arrived in {receipts.length} {receipts.length === 1 ? 'delivery' : 'deliveries'}:{' '}
            {receipts.map((r) => `${r.reference} on ${r.received_on}`).join(', ')}. Part now and
            part next week is one delivery, not a failure.
          </Note>
        )}

        {can('proc.grn.create') && !receivable && (
          <Note kind="info">
            Goods can be received once this order is approved and issued to the vendor. It is{' '}
            {order.status.replace(/_/g, ' ')} now.
          </Note>
        )}
      </section>

      {canReceive && (
        <section>
          <h3>Record what arrived</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              receive.mutate()
            }}
          >
            <div className="row">
              <Field label="Received on">
                <input
                  type="date"
                  value={receivedOn}
                  onChange={(e) => setReceivedOn(e.target.value)}
                  required
                />
              </Field>
              <Field label="Delivery note" caption="the supplier's docket number">
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
              <div className="shrink">
                <button className="primary" type="submit" disabled={receive.isPending}>
                  Record receipt
                </button>
              </div>
            </div>
            <Note kind="info">
              Recording a receipt posts nothing. It records what arrived, so the bill can be checked
              against it rather than against the order alone. Type the quantities in the last column
              above.
            </Note>
          </form>
        </section>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------

function LineEditor({
  lines,
  setLines,
  rateLabel,
  withTax = false,
}: {
  lines: EditableLine[]
  setLines: (lines: EditableLine[]) => void
  rateLabel: string
  //: An order carries GST per line; a request is an estimate and does not.
  withTax?: boolean
}) {
  const update = (i: number, patch: Partial<EditableLine>) =>
    setLines(lines.map((l, j) => (i === j ? { ...l, ...patch } : l)))

  return (
    <>
      <div className="scroll-x">
        <table style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Description</th>
              <th className="num" style={{ width: 110 }}>
                Quantity
              </th>
              <th className="num" style={{ width: 150 }}>
                {rateLabel} (₹)
              </th>
              <th className="num" style={{ width: 150 }}>
                Amount
              </th>
              {withTax && (
                <>
                  <th className="num" style={{ width: 100 }}>
                    GST %
                  </th>
                  <th className="num" style={{ width: 130 }}>
                    GST (₹)
                  </th>
                  <th className="num" style={{ width: 150 }}>
                    With GST
                  </th>
                </>
              )}
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td>
                  <input
                    value={line.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                    required
                  />
                </td>
                <td>
                  <input
                    className="num"
                    value={line.quantity}
                    onChange={(e) => update(i, { quantity: e.target.value })}
                    required
                  />
                </td>
                <td>
                  <input
                    className="num"
                    value={line.rate}
                    onChange={(e) => update(i, { rate: e.target.value })}
                    placeholder="0.00"
                  />
                </td>
                <td className="num muted">{formatInr(lineAmount(line))}</td>
                {withTax && (
                  <>
                    <td>
                      <input
                        className="num"
                        list="gst-slabs"
                        value={line.gst}
                        onChange={(e) => update(i, { gst: e.target.value })}
                        placeholder="—"
                      />
                    </td>
                    <td className="num muted">{formatInr(lineTax(line))}</td>
                    <td className="num">{formatInr(lineAmount(line) + lineTax(line))}</td>
                  </>
                )}
                <td className="num">
                  {lines.length > 1 && (
                    <button
                      type="button"
                      className="small"
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {withTax && (
        <datalist id="gst-slabs">
          {GST_SLABS.map((slab) => (
            <option key={slab} value={slab} />
          ))}
        </datalist>
      )}
      <div className="row">
        <div className="shrink">
          <button type="button" className="small" onClick={() => setLines([...lines, BLANK])}>
            Add a line
          </button>
        </div>
      </div>
    </>
  )
}
